import { APPLICATION_RELATIONSHIP } from "@lending/types";
import type {
  ApplicationApplicantDetailsRead,
  LendingApplicationApplicantDetails,
} from "@lending/types";

/**
 * Applicant selection helpers.
 *
 * These centralize two rules that were previously duplicated across route
 * layouts (`applicationNavigationContext`, `resumeRouteResolver`) and page
 * hydration paths (`Address`, `PersonalInfo`):
 *
 * - `getCoApplicant(application)` picks the co-applicant off an application's
 *   `additional_applicants`, which by contract never contains the primary.
 * - `findPrimaryApplicant(applicants)` picks the primary from a flat applicant
 *   list (as returned by `useListApplicationApplicantDetailsQuery`), falling
 *   back to the first entry so responses that predate
 *   `application_relationship` still hydrate the primary.
 */

type Applicant = LendingApplicationApplicantDetails;

export function getCoApplicant(
  application:
    | {
        additional_applicants?: readonly Applicant[] | null;
      }
    | null
    | undefined
): Applicant | undefined {
  return application?.additional_applicants?.find(
    (applicant) => applicant.application_relationship === APPLICATION_RELATIONSHIP.CO_APPLICANT
  );
}

export function findPrimaryApplicant<
  T extends { application_relationship?: string | null | undefined },
>(applicants: readonly T[] | null | undefined): T | undefined {
  return (
    applicants?.find(
      (applicant) => applicant.application_relationship === APPLICATION_RELATIONSHIP.PRIMARY
    ) ?? applicants?.[0]
  );
}

// Submission requires both, and the OLB flows only collect them on confirm-info's edit drawer.
export function isMissingContactDetails(
  applicant: Pick<ApplicationApplicantDetailsRead, "phone" | "residential_address">
): boolean {
  return !applicant.phone || !applicant.residential_address;
}
